import clsx from "clsx";
import { useRef, type ChangeEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

export type SidebarBrandingModel = {
  entityKey: string;
  shortName: string;
  logoUrl?: string;
  fullName?: string;
};
type SidebarBrandingProps = {
  branding: SidebarBrandingModel;
  collapsed?: boolean;
  rightSlot?: ReactNode;
  onLogoUrlChange?: (nextLogoUrl: string | undefined) => void;
  homeHref?: string;
};

export function SidebarBranding({
  branding,
  collapsed,
  rightSlot,
  onLogoUrlChange,
  homeHref = "/dashboard",
}: SidebarBrandingProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
    onLogoUrlChange?.(dataUrl);
    event.target.value = "";
  };
  return (
    <div
      className={clsx(
        "relative flex shrink-0 flex-col items-center justify-center gap-3 border-b border-slate-200 bg-brand-sidebar-bg dark:border-slate-800 dark:bg-slate-950",
        collapsed ? "px-2 py-3" : "px-4 py-6",
      )}
    >
      <Link
        to={homeHref}
        aria-label={`${branding.fullName ?? branding.shortName} home`}
        title={branding.fullName ?? branding.shortName}
        className="absolute inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        aria-label="Upload school logo"
        title="Change school logo"
        className={clsx(
          "relative flex shrink-0 items-center justify-center overflow-hidden bg-white ring-1 ring-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:ring-slate-700",
          collapsed ? "h-12 w-12 rounded-xl" : "h-16 w-16 rounded-2xl",
        )}
      >
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt="" className="h-full w-full object-contain" />
        ) : (
          <span
            className={clsx(
              "font-bold text-slate-700",
              collapsed ? "text-sm" : "text-2xl",
            )}
          >
            {branding.shortName}
          </span>
        )}
      </button>
      {!collapsed && branding.logoUrl && branding.shortName.length >= 2 && (
        <span className="max-w-full truncate text-sm font-bold tracking-widest text-slate-900 dark:text-white">
          {branding.shortName}
        </span>
      )}
      {!collapsed && rightSlot && (
        <div className="absolute right-3 top-3">{rightSlot}</div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label="School logo file"
        onChange={onFileChange}
      />
    </div>
  );
}
